import React, { MouseEventHandler, useCallback, useEffect, useMemo } from "react";
import {
	DataGridProProps,
	GridRowClassNameParams,
	GridRowId,
	useGridApiRef,
} from "@mui/x-data-grid-pro";
import { StyledDataGrid } from "src/components/common/dataGrid/styles/StyledDataGrid.tsx";
import { filterNulls } from "src/utils/arrays";
import { GridProSlotProps } from "@mui/x-data-grid-pro/models/gridProSlotProps";
import { useContextMenu } from "src/components/common/contextMenu/useContextMenu.ts";
import { AavoContextMenu } from "../contextMenu/AavoContextMenu";
import { mergeRowClassNames } from "src/components/common/dataGrid/utils.ts";
import { dataGridRowClassNames } from "src/components/common/dataGrid/styles/dataGridClassNames.ts";
import { Box, useTheme } from "@mui/material";
import { mergeSx } from "src/utils/styles.ts";
import { AavoActionBar } from "src/components/common/actionBar/AavoActionBar.tsx";
import { AavoIconButton } from "src/components/common/buttons/AavoIconButton.tsx";
import { faRefresh } from "@fortawesome/pro-regular-svg-icons";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { AavoDataGridApi } from "src/components/common/dataGrid/AavoDataGridApi.ts";
import { useMaybeControlledState } from "src/utils/useMaybeControlledState.ts";

export interface AavoDataGridProps<RowData extends object>
	extends Omit<
			DataGridProProps<RowData>,
			| "onRowSelectionModelChange"
			| "rowSelectionModel"
			| "initialState"
			| "getRowClassName"
			| "columns"
			| "apiRef"
			| "getRowId"
		>,
		Required<Pick<DataGridProProps<RowData>, "initialState" | "getRowId">> {
	columns: AavoGridColDef<RowData>[];
	selectedRows?: RowData[];
	onRowSelectionChanged?: (selectedRows: RowData[]) => void;
	onRefresh?: () => Promise<unknown>;
	actionBarComponents?: React.ReactNode;
	rowContextMenuComponents?: (
		params: AavoDataGridRowContextMenuParams<RowData>,
	) => React.ReactNode | Promise<React.ReactNode>;
	getRowClassNames?: (params: GridRowClassNameParams<RowData>) => string[] | string | undefined;
	apiRef?: React.MutableRefObject<AavoDataGridApi | null>;
}

export type AavoGridColDef<RowData extends object> = GridColDef<RowData> | false | undefined; // false and undefined are used to hide columns

export interface AavoDataGridRowContextMenuParams<RowData extends object> {
	row: RowData;
	allSelectedRows: RowData[];
	multipleRowsSelected: boolean;
	onlySingleRowSelected: boolean;
}

export const AavoDataGrid = <RowData extends object>({
	apiRef: forwardedApiRef,
	selectedRows: selectedRowsProp,
	onRowSelectionChanged,
	hideFooter = true,
	actionBarComponents,
	onRefresh: onRefreshProp,
	rowContextMenuComponents,
	slotProps,
	getRowClassNames,
	columns,
	getRowId,
	sx,
	...other
}: AavoDataGridProps<RowData>) => {
	const gridApiRef = useGridApiRef();
	const theme = useTheme()

	const [openContextMenu, contextMenuState] = useContextMenu();

	const rowSelectionModelControlled = useMemo(
		() => selectedRowsProp?.map((row) => getRowId(row)),
		// getRowId assumed to be stable
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedRowsProp],
	);
	const [rowSelectionModel, setRowSelectionModel] = useMaybeControlledState<GridRowId[]>({
		controlledValue: rowSelectionModelControlled,
		defaultValue: [],
	});

	const getGridApi = useCallback(() => {
		const current = gridApiRef.current;
		if (current === null) throw new Error("GridApiRef has no value");

		return current;
	}, [gridApiRef]);

	const getRowsByIds = useCallback(
		(ids: GridRowId[]): RowData[] =>
			filterNulls(
				ids.map((id) => {
					const row = gridApiRef.current?.getRow(id);
					if (row === undefined) return undefined;
					return row;
				}),
			),
		[gridApiRef],
	);

	const onRowSelectionModelChange = useCallback(
		(selectionModel: GridRowId[]) => {
			setRowSelectionModel(selectionModel);

			if (onRowSelectionChanged) {
				const selectedRows = getRowsByIds(selectionModel);
				onRowSelectionChanged(selectedRows);
			}
		},
		[setRowSelectionModel, onRowSelectionChanged, getRowsByIds],
	);

	const getRowClassName = useCallback(
		(params: GridRowClassNameParams): string => {
			let fromProps = getRowClassNames?.(params) ?? [];
			if (typeof fromProps === "string") fromProps = [fromProps];
			return mergeRowClassNames(
				...fromProps,
				params.indexRelativeToCurrentPage % 2 === 0
					? dataGridRowClassNames.even
					: dataGridRowClassNames.odd,
			);
		},
		[getRowClassNames],
	);

	const onContextMenu: MouseEventHandler<any> = useCallback(
		(event) => {
			if (event.type === "contextmenu" && rowContextMenuComponents !== undefined) {
				event.preventDefault();
				const rowId = Number(event.currentTarget.getAttribute("data-id"));
				const gridApi = getGridApi();
				if (rowId == null || gridApiRef.current === undefined) return;
				const row = gridApi.getRow(rowId);
				if (row == null) return;

				let selectedRowIds = rowSelectionModel;
				const clickedRowIsSelected = rowSelectionModel.includes(rowId);
				if (!clickedRowIsSelected) {
					selectedRowIds = [rowId];
					setRowSelectionModel(selectedRowIds);
					onRowSelectionModelChange(selectedRowIds);
				}
				const selectedRows = getRowsByIds(selectedRowIds);

				openContextMenu({
					content: () =>
						rowContextMenuComponents({
							row: row,
							allSelectedRows: selectedRows,
							multipleRowsSelected: selectedRows.length > 1,
							onlySingleRowSelected: selectedRows.length === 1,
						}),
					mouseEvent: event,
				});
			}
		},
		[
			rowContextMenuComponents,
			getGridApi,
			gridApiRef,
			rowSelectionModel,
			getRowsByIds,
			openContextMenu,
			onRowSelectionModelChange,
			setRowSelectionModel,
		],
	);

	const slotPropsWrapped: GridProSlotProps = useMemo(
		() => ({
			...slotProps,
			row: {
				...slotProps?.row,
				onContextMenu: onContextMenu,
			},
			baseSelect: {
				...slotProps?.baseSelect,
				MenuProps: {
					...slotProps?.baseSelect?.MenuProps,
					// Don't terminate editing when selecting value with Enter on select column
					onKeyDown: (e) => {
						if (e.key === "Enter") {
							e.stopPropagation();
						}
					},
				},
			},
		}),
		[slotProps, onContextMenu],
	);

	const hasActionBar = actionBarComponents !== undefined || onRefreshProp !== undefined;

	const onRefresh = async () => {
		if (!onRefreshProp) return;

		await onRefreshProp();

		// Trigger row selection event, because content of selected rows may have been changed.
		// Delay it to next tick to get refreshed data.
		// TODO is this needed any longer? useServerSideDataModel now handles this itself.
		setTimeout(() => {
			if (onRowSelectionChanged) {
				const selectedRows = getRowsByIds(rowSelectionModel);
				onRowSelectionChanged(selectedRows);
			}
		}, 1);
	};

	useEffect(() => {
		if (forwardedApiRef)
			forwardedApiRef.current = {
				...gridApiRef.current,
			};
	});

	return (
		<Box
			sx={mergeSx(
				{
					display: "flex",
					flexDirection: "column",
					flex: 1,
				},
				sx,
			)}
		>
			{hasActionBar && (
				<AavoActionBar>
					{onRefreshProp && (
						<AavoIconButton icon={faRefresh} color={"primary"} onClick={onRefresh} />
					)}
					{actionBarComponents}
				</AavoActionBar>
			)}
			<StyledDataGrid
				apiRef={gridApiRef}
				columns={filterHiddenColumns(columns)}
				pageSizeOptions={PAGE_SIZE_OPTIONS}
				rowSelectionModel={rowSelectionModel}
				onRowSelectionModelChange={onRowSelectionModelChange}
				hideFooter={hideFooter}
				slotProps={slotPropsWrapped}
				getRowClassName={getRowClassName}
				getRowId={getRowId}
				rowHeight={theme.typography.fontSize * 2.5}
				columnHeaderHeight={theme.typography.fontSize * 2.125}
				{...other}
			/>
			<AavoContextMenu state={contextMenuState} />
		</Box>
	);
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 150];

const filterHiddenColumns = <RowData extends object>(
	columns: AavoGridColDef<RowData>[],
): GridColDef<RowData>[] => {
	return columns.filter(
		(column) => column !== false && column !== undefined,
	) as GridColDef<RowData>[];
};
